<template>
  <CommunityCard class="reg-container">
    <form class="regform" v-if="!hasRegistered">
      <div class="mb-4">
        <img
          class="mb-3"
          style="width: 200px"
          src="../assets/iSimulate-Logo.svg"
        />
        <h5>Join the iSimulate community</h5>
      </div>

      <div class="form-group">
        <label for="firstname">Your Details</label>
        <input
          type="text"
          v-model="firstName"
          :class="{ 'form-control mb-2': true, 'is-invalid': !firstNameValid }"
          id="firstname"
          placeholder="First name"
        />
        <input
          type="text"
          v-model="lastName"
          :class="{ 'form-control mb-2': true, 'is-invalid': !lastNameValid }"
          id="lastname"
          placeholder="Last name"
        />
        <input
          type="email"
          v-model="email"
          :class="{ 'form-control mb-2': true, 'is-invalid': !emailValid }"
          id="email"
          placeholder="name@example.com"
        />

        <input
          type="text"
          v-model="organisationCode"
          class="form-control mb-2"
          id="organisationCode"
          placeholder="Organisation ID (optional)"
        />
      </div>
      <div class="form-group">
        <label for="username">Your Account</label>
        <input
          type="text"
          v-model="username"
          :class="{ 'form-control mb-2': true, 'is-invalid': !usernameValid }"
          id="username"
          placeholder="Username"
        />
        <input
          type="password"
          v-model="password"
          placeholder="Password"
          :class="{
            'form-control mb-2': true,
            'is-invalid': !passwordValid || !passwordsMatch,
          }"
          id="password1"
        />
        <input
          type="password"
          v-model="password2"
          placeholder="Confirm Password"
          :class="{
            'form-control mb-2': true,
            'is-invalid': !passwordValid || !passwordsMatch,
          }"
          class="form-control"
          id="password2"
        />
        <small id="passwordHelpBlock" class="form-text text-muted">
          Your password must be at least 8 characters long, containing at least
          one uppercase, lowercase and number.
        </small>
      </div>

      <button
        class="btn btn-lg btn-block mt-4 mb-2 reg-btn"
        type="submit"
        @click.prevent="submit()"
      >
        {{ buttonText }}
      </button>

      <div class="text-center">
        <small class="form-text text-muted">
          By registering, you agree to our
          <router-link tag="span" class="small-link" to="/terms">
            Terms and Conditions
          </router-link>
        </small>
        <small class="form-text text-muted">
          Already have an account?
          <router-link tag="span" class="small-link" to="/login">
            Sign In
          </router-link>
        </small>
        <small class="server-err" v-if="serverError">
          There was an error: {{ serverError }}
        </small>
      </div>
    </form>
    <div class="regform" v-else>
      <div>
        <img
          class="mb-4"
          style="width: 200px"
          src="../assets/iSimulate-Logo.svg"
        />
        <h6>Thanks for registering!</h6>
      </div>
      <small>
        Please check your email inbox and click the link to verify your email
        address.
      </small>
      <small class="form-text text-muted" style="margin-top: 16px">
        <router-link tag="span" class="small-link" to="/login">
          Back to Sign In page
        </router-link>
      </small>
    </div>
  </CommunityCard>
</template>

<script>
import CommunityCard from "../components/CommunityCard";
import userService from "../services/user.service";

export default {
  name: "Register",
  components: {
    CommunityCard,
  },
  data() {
    return {
      buttonText: "Register",
      firstName: "",
      lastName: "",
      email: "",
      username: "",
      password: "",
      password2: "",
      organisationCode: "",
      firstNameValid: true,
      lastNameValid: true,
      emailValid: true,
      usernameValid: true,
      passwordValid: true,
      passwordsMatch: true,
      serverError: "",
      hasRegistered: false,
      submitting: false,
    };
  },
  computed: {
    currentDistributor() {
      return this.$store.getters.getDistributor;
    },
  },
  mounted() {
    const orgCode = new URLSearchParams(window.location.search).get("orgcode");
    if (orgCode) this.organisationCode = orgCode;
  },
  methods: {
    submit() {
      if (this.submitting) return;
      this.submitting = true;

      this.firstNameValid =
        this.lastNameValid =
        this.emailValid =
        this.usernameValid =
        this.passwordValid =
        this.passwordsMatch =
          true;

      this.firstName = this.firstName.trim();
      this.lastName = this.lastName.trim();
      this.email = this.email.trim().toLowerCase();
      this.username = this.username.trim();

      if (!this.validate()) {
        this.submitting = false;
        return;
      }

      this.buttonText = "...";

      const userObj = {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        username: this.username,
        password: this.password,
        distributorId: this.currentDistributor,
      };

      if (this.organisationCode)
        userObj.organisationCode = this.organisationCode;

      userService
        .registerNew(userObj)
        .then(() => (this.hasRegistered = true))
        .catch((err) => (this.serverError = err))
        .finally(() => {
          this.buttonText = "Register";
          this.submitting = false;
        });
    },
    validate() {
      this.firstNameValid = this.firstName.length > 0;
      this.lastNameValid = this.lastName.length > 0;
      this.emailValid =
        this.email.length && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.email);
      this.usernameValid = this.username.length && !/\s/.test(this.username);
      // 8 characters, one uppercase, lowercase and number
      this.passwordValid =
        this.password.length &&
        /^(?=.*?[A-Z])(?=(.*[a-z]){1,})(?=(.*[\d]){1,})(?!.*\s).{8,}$/.test(
          this.password
        );
      this.passwordsMatch = this.password === this.password2;

      return (
        this.firstNameValid &&
        this.lastNameValid &&
        this.emailValid &&
        this.usernameValid &&
        this.passwordValid &&
        this.passwordsMatch
      );
    },
  },
};
</script>

<style scoped>
h5 {
  color: var(--isim-primary);
}

.reg-container {
  max-width: 600px;
  margin: 7vh auto;
}

.regform {
  margin: 40px auto;
  max-width: 340px;
}

.small-link {
  text-decoration: underline;
  cursor: pointer;
}

.server-err {
  color: red;
}

.reg-btn {
  background: var(--isim-primary);
  color: white;
}
</style>
